// src/api/transformOutlook.ts
//
// Transform outlook — "what happens to my data models now, and when?"
// (plan 2026-08-02-transform-only-source-creation-dispatch-event, S4–S6).
//
// The customer-facing mirror of the backend's dispatch decision: after a
// change that moves the source set (create / pause / resume / delete /
// categories), this says what the platform is going to do about it. Read-only
// and account-scoped; a VIEWER may read it.

import type { components } from "@verity/shared-types";
import { apiJson } from "./client";

/**
 * Generated from the backend's `TransformOutlookResponse`. Fields worth knowing
 * about beyond the dispatch-decision mirror (`state` and friends):
 *
 * - `sources_awaiting_first_data`: sources that contributed no data to the
 *   latest run because none of their raw tables had landed yet.
 * - `floor_local_time` / `floor_tz` / `observed_arrival_local_time`: the
 *   resolved daily promise for the connector, one deadline for all of its
 *   sources. Never derive them client-side.
 * - `manual_requested_at`: a manual refresh that no run has picked up yet
 *   (plan 2026-09-26-source-actions-show-requested-immediately). Clears on
 *   the start that honours it. Connector-wide, like the run it announces.
 *
 * Nullable fields are null for `NOT_APPLICABLE` connectors.
 */
export type TransformOutlook = components["schemas"]["TransformOutlookResponse"];

/** GET /managed-sources/:id/transform-outlook */
export async function getTransformOutlook(
  managedSourceId: string,
): Promise<TransformOutlook> {
  return apiJson<TransformOutlook>(
    `/managed-sources/${encodeURIComponent(managedSourceId)}/transform-outlook`,
  );
}

/**
 * GET /managed-sources/transform-outlook/by-connector?connector_type=X
 *
 * The form for flows that have no source id to ask about: after a DELETE the
 * source is gone (the per-source form would 404), and right after a creation
 * the answer is about the connector as a whole anyway.
 */
export async function getTransformOutlookByConnector(
  connectorType: string,
): Promise<TransformOutlook> {
  const q = new URLSearchParams({ connector_type: connectorType });
  return apiJson<TransformOutlook>(
    `/managed-sources/transform-outlook/by-connector?${q.toString()}`,
  );
}
